import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import { getSettingsStyles } from '../../styles/settingsStyles'
import Navigation from '../settings-navigation/Navigation'
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { CircularProgress, Typography, Switch } from '@mui/material'
import { deleteSetup, getAllSetups } from '../../redux/actions/setups'
import { getAllUsers } from '../../redux/actions/users'
import { CustomButton } from '../../components/restyled-mui/CustomButton'
import { useDispatch } from 'react-redux'
import { changeStatus } from '../../redux/actions/status'
import { STATUS } from '../../redux/types'
import Api from '../../api'

import ConfirmationDialog from '../confirmation-dialog/Confirmation'
import DeleteIcon from '@mui/icons-material/Delete'

const useStyles = makeStyles(theme => ({
  ...getSettingsStyles(theme),
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
    padding: '50px 0 20px 0',
  },

  container: {
    textAlign: 'center',
    width: '95%',
    margin: '0 auto',
    height: '85vh',
  },
  disabledRow: {
    backgroundColor: '#f0f0f0 !important', // light gray
    opacity: 0.6,
    pointerEvents: 'none', // disable clicks
    userSelect: 'none',
  },
  deleteIcon: {
    color: '#d32f2f',
    cursor: 'pointer',
    transition: 'color 0.2s ease, transform 0.1s ease',
    pointerEvents: 'auto',
    '&:hover': {
      color: '#b71c1c', // darker red
      transform: 'scale(1.1)', // small feedback animation
    },
  },
}))

const overridesWeirdResizing = {
  '& .MuiDataGrid-main': {
    width: 0,
    minWidth: '100%',
  },
  // Remove the default pointer on row hover
  '& .MuiDataGrid-row:hover': {
    cursor: 'default',
  },

  // Only show pointer for the first cell (NAME column)
  '& .MuiDataGrid-cell:first-of-type:hover': {
    cursor: 'pointer',
    color: '#1976d2', // same as MUI primary blue
    fontWeight: 500,
    transition: 'color 0.2s ease',
    backgroundColor: 'rgba(25, 118, 210, 0.06)', // light blue hover tint
  },

  // Only show pointer for cells that contain the Switch or Delete icon
  '& .MuiDataGrid-cell:has(.MuiSwitch-root:hover), & .MuiDataGrid-cell:has(.MuiSvgIcon-root:hover)':
    {
      cursor: 'pointer',
    },
}

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      {/* <GridToolbarFilterButton /> */}
      <GridToolbarQuickFilter
        debounceMs={300} // Smooth typing experience
        placeholder="Search Setup..." // Custom placeholder
      />
    </GridToolbarContainer>
  )
}

// Column builder
const createColumns = (key, renderStatusCell, renderDeleteCell) => [
  {
    field: key,
    headerName: 'NAME',
    flex: 3,
    disableColumnMenu: true,
    headerClassName: 'super-app-theme--header',
  },
  {
    field: 'enabled',
    headerName: 'STATUS',
    flex: 1.5,
    headerClassName: 'super-app-theme--header',
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: renderStatusCell,
  },
  {
    field: 'delete',
    headerName: '',
    flex: 1.5,
    headerClassName: 'super-app-theme--header',
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: renderDeleteCell,
  },
]

const initialConfirmDialog = {
  open: false,
  confirmCallback: null,
  cancelCallback: null,
  title: '',
  content: '',
  color: '',
  type: 'default',
  expectedText: null,
  confirmLabel: 'Delete',
  cancelLabel: 'Cancel',
}

const Setups = props => {
  const { history, allSetups, getAllSetups, userPermissions, deleteSetup } =
    props
  const classes = useStyles(props)
  const location = useLocation()
  const dispatch = useDispatch()
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    confirmCallback: null,
    cancelCallback: null,
    title: '',
    content: '',
    color: '',
  })

  const isAdmin = userPermissions.includes('settings')
  const nameKey = isAdmin ? 'name' : 'alias'

  const createRenderStatusCell = ({ dispatch, getAllSetups, key }) => {
    return params => {
      const handleToggle = async event => {
        event.stopPropagation()
        const setupId = params.row.id
        const setupName = params.row[key]
        const isEnabling = !params.value

        // If enabling, no need for confirmation
        if (isEnabling) {
          executeToggle(setupId, setupName)
          return
        }

        // If disabling, check affected users first
        try {
          const response = await Api.Users.checkUserSetups(setupId)
          const affectedUsers = response.data.user_ids || []

          setConfirmDialog({
            open: true,
            confirmCallback: async () => {
              try {
                // First disable all affected users if any
                if (affectedUsers.length > 0) {
                  await Promise.all(
                    affectedUsers.map(user => {
                      return Api.Users.updateUser({
                        user_id: user.user_id,
                        enabled: false,
                      })
                    }),
                  )
                  // Refresh users list after disabling affected users
                  await dispatch(getAllUsers())
                }

                // Then disable the setup
                await executeToggle(setupId, setupName)
                setConfirmDialog(prev => ({ ...prev, open: false }))
              } catch (error) {
                console.error('Error in disable operation:', error)
                dispatch(
                  changeStatus(
                    STATUS.ERROR,
                    error.response?.data?.msg ||
                      'Failed to complete disable operation',
                  ),
                )
                setConfirmDialog(prev => ({ ...prev, open: false }))
              }
            },
            cancelCallback: () =>
              setConfirmDialog(prev => ({ ...prev, open: false })),
            title: 'Disable Setup',
            content: (
              <>
                {affectedUsers.length > 0 ? (
                  <>
                    <Typography gutterBottom>
                      If you disable <b>{setupName}</b>, the following users
                      will also be disabled:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {affectedUsers.map(user => (
                        <li key={user.user_id}>
                          <Typography variant="body2">
                            {user.user_name}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                    <Typography
                      variant="body2"
                      color="error"
                      style={{ marginTop: '16px' }}
                    >
                      Are you sure you want to proceed?
                    </Typography>
                  </>
                ) : (
                  <Typography>
                    Are you sure you want to disable <b>{setupName}</b>?
                  </Typography>
                )}
              </>
            ),
            color: '#C63C3C',
            type: 'warning',
          })
        } catch (error) {
          console.error('Check affected users failed:', error)
          dispatch(
            changeStatus(
              STATUS.ERROR,
              error.response?.data?.msg || 'Failed to check affected users',
            ),
          )
        }
      }

      const executeToggle = async (setupId, setupName) => {
        try {
          const response = await Api.Setups.toggleSetupStatus(setupId)

          if (response.status === 200) {
            await Promise.resolve(getAllSetups()) // triggers dispatch, wait microtask

            // small safety delay so the DataGrid re-renders before showing toast
            setTimeout(() => {
              dispatch(
                changeStatus(
                  STATUS.SUCCESS,
                  response.data?.msg ||
                    `The status of ${setupName} was successfully updated.`,
                ),
              )
            }, 100)
          } else {
            throw new Error(response.data?.msg || 'Unexpected error occurred.')
          }
        } catch (error) {
          console.error('Toggle setup status failed:', error)
          dispatch(
            changeStatus(
              STATUS.ERROR,
              error.response?.data?.msg || 'Failed to update setup status',
            ),
          )
          throw error // Re-throw to handle in the calling function
        }
      }

      // Optionally skip toggle for specific setup IDs (like id === 1)
      // if (params.row.id === 1) return null

      return (
        <div
          style={{
            pointerEvents: 'auto',
            padding: '2px 4px',
            borderRadius: 6,
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={e =>
            (e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)')
          }
          onMouseLeave={e =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          <Switch
            checked={params.value}
            onClick={handleToggle}
            color="primary"
            size="small"
          />
        </div>
      )
    }
  }

  const createRenderDeleteCell = ({ key }) => {
    return params => {
      const setupId = params.row.id
      const setupName = params.row[key]

      const handleDelete = event => {
        event.stopPropagation()
        setConfirmDialog({
          open: true,
          confirmCallback: () => {
            deleteSetup(setupId, setupName)
            setConfirmDialog(initialConfirmDialog)
          },
          cancelCallback: () => setConfirmDialog(initialConfirmDialog),
          title: 'Delete Setup',
          content: (
            <Typography>
              Please type <b>{setupName}</b> to confirm deletion.
            </Typography>
          ),
          color: '#C63C3C',
          type: 'warning',
          expectedText: setupName,
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
        })
      }

      return (
        <div
          className={classes.deleteIcon}
          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
          onClick={handleDelete}
        >
          <DeleteIcon />
        </div>
      )
    }
  }

  const renderStatusCell = useMemo(
    () => createRenderStatusCell({ dispatch, getAllSetups, key: nameKey }),
    [dispatch, getAllSetups, nameKey],
  )

  const renderDeleteCell = useMemo(
    () => createRenderDeleteCell({ dispatch, getAllSetups, key: nameKey }),
    [dispatch, getAllSetups, nameKey],
  )

  const columns = useMemo(
    () => createColumns(nameKey, renderStatusCell, renderDeleteCell),
    [nameKey, renderStatusCell, renderDeleteCell],
  )

  useEffect(() => {
    if (!allSetups) {
      getAllSetups()
    }
  }, [allSetups, getAllSetups])

  const handleCreateSetup = () => {
    history.push(`${location.pathname}/create`)
  }

  const editSetup = params => {
    if (['enabled', 'delete'].includes(params.field)) return

    if (!params.row.enabled) return

    const setup = allSetups.find(setup => setup.id === params.row.id)
    history.push(`${location.pathname}/${setup.name}`)
  }

  return (
    <Navigation history={history}>
      <ConfirmationDialog {...confirmDialog} />
      {allSetups ? (
        <div className={classes.container}>
          <div className={classes.headerContainer}>
            {/* <CustomButton
              onClick={handleCreateSetup}
              variant="contained"
              disableElevation
              color="primary"
              className={classes.addNewButton}
            >
              Add new
            </CustomButton> */}
          </div>

          <DataGrid
            rows={allSetups}
            rowHeight={30}
            columns={columns}
            getRowId={row => row.id}
            disableColumnSelector
            onCellClick={editSetup}
            autoPageSize
            getRowClassName={params =>
              !params.row.enabled ? classes.disabledRow : ''
            }
            slots={{
              toolbar: props => <CustomToolbar {...props} />,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true, // Enables search bar
                quickFilterProps: { debounceMs: 300 },
              },
            }}
            sx={overridesWeirdResizing}
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </div>
      )}
    </Navigation>
  )
}

const mapStateToProps = state => ({
  allSetups: state.setups,
  userPermissions: state.userPermissions.permissions,
})

export default connect(mapStateToProps, {
  getAllSetups,
  getAllUsers,
  deleteSetup,
})(Setups)
